import React from 'react';
import RobbieMark from './RobbieMark';

/**
 * CorrectionBar (TASK-104) — the table-footer bar under AI-extracted data
 * ("Does something look wrong or missing? Tell me and I'll correct it." +
 * Fix with Robbie). Presentational only: pass it to `Table`'s `footer` slot;
 * wiring the Robbie action to real chat is TASK-304. Violet button = Robbie
 * acts for you (the deliberate exception to Button's no-violet rule); the
 * secondary button stays a plain user action.
 */
export interface CorrectionBarProps {
  /** Robbie's line — first person, defaults to the BOM-modal copy. */
  message?: React.ReactNode;
  /** Violet Robbie action (default "Fix with Robbie"). */
  actionLabel?: string;
  /** Omit and the Robbie action renders disabled — never a button that does nothing. */
  onAction?: () => void;
  /** Disable the Robbie action even with a handler; `actionHint` says why. */
  actionDisabled?: boolean;
  actionHint?: string;
  /** Optional plain secondary action, e.g. "Done". */
  secondaryLabel?: string;
  onSecondary?: () => void;
  /** Trailing user actions after the secondary one, e.g. a primary Save. */
  children?: React.ReactNode;
  className?: string;
}

export const CorrectionBar: React.FC<CorrectionBarProps> = ({
  message = "Does something look wrong or missing? Tell me and I'll correct it.",
  actionLabel = 'Fix with Robbie',
  onAction,
  actionDisabled = false,
  actionHint,
  secondaryLabel,
  onSecondary,
  children,
  className = '',
}) => (
  <div className={`flex flex-wrap items-center gap-s4 border-t border-robbie-border bg-ai-panel px-s6 py-s4 ${className}`}>
    <RobbieMark size="xs" aria-label="Robbie" />
    <span className="min-w-0 flex-1 basis-[220px] text-body-sm text-ink-body">{message}</span>
    <button
      type="button"
      onClick={onAction}
      disabled={!onAction || actionDisabled}
      title={actionHint}
      className="ml-auto inline-flex h-7 shrink-0 items-center whitespace-nowrap rounded-control
        bg-robbie px-s4 text-body-sm font-semibold text-white transition-opacity hover:opacity-90
        focus:outline-none focus-visible:ring-2 focus-visible:ring-robbie focus-visible:ring-offset-1
        disabled:cursor-not-allowed disabled:opacity-50"
    >
      {actionLabel}
    </button>
    {secondaryLabel && (
      <button
        type="button"
        onClick={onSecondary}
        className="inline-flex h-7 shrink-0 items-center whitespace-nowrap rounded-control border
          border-line bg-surface px-s4 text-body-sm font-semibold text-ink hover:bg-surface-hover
          focus:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-1"
      >
        {secondaryLabel}
      </button>
    )}
    {children}
  </div>
);

export default CorrectionBar;
